import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Button,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Dropdown,
  Badge,
} from "react-bootstrap";
import { useLanguage } from "../context/LanguageContext.js";
import useWebsiteSubmissions from "../hooks/useWebsiteSubmissions.js";
import { SERVICE_NAMES, formatDate } from "../services/itsSoCleanApi.js";
import "./WebsiteSubmissionsPage.css";

export default function WebsiteSubmissionsPage() {
  const { language } = useLanguage();
  const {
    stats,
    conversations,
    filters,
    loading,
    loadingSubmissions,
    loadingDetails,
    sendingReply,
    error,
    selectedConversation,
    selectedSubmissionReplies,
    selectConversation,
    closeDetails,
    sendReply,
    archiveConversation,
    markConversationAsUnread,
    updateFilters,
    refresh,
  } = useWebsiteSubmissions();

  const [replyMessage, setReplyMessage] = useState("");
  const [replySuccess, setReplySuccess] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(null);
  const replyInputRef = useRef(null);
  const detailBodyRef = useRef(null);

  // Textos multilíngue
  const t = {
    pt: {
      title: "Mensagens",
      subtitle: "Website It's So Clean",
      all: "Todas",
      new: "Novas",
      replied: "Respondidas",
      archived: "Arquivadas",
      search: "Buscar mensagens...",
      noMessages: "Nenhuma mensagem",
      noMessagesDesc: "Quando alguém enviar uma mensagem pelo website, ela aparecerá aqui.",
      selectMessage: "Selecione uma conversa",
      selectMessageDesc: "Escolha um cliente da lista para ver as mensagens.",
      customer: "Cliente",
      address: "Endereço",
      service: "Serviço",
      frequency: "Frequência",
      property: "Imóvel",
      bedrooms: "quartos",
      bathrooms: "banheiros",
      carpetedArea: "Área carpetada",
      message: "Mensagem",
      messages: "mensagens",
      replies: "Respostas",
      replyPlaceholder: "Escreva sua resposta...",
      send: "Enviar",
      sending: "Enviando...",
      replySent: "Resposta enviada!",
      markUnread: "Marcar como não lida",
      archive: "Arquivar conversa",
      confirmArchive: "Arquivar todas as mensagens?",
      yes: "Sim",
      no: "Não",
      today: "Hoje",
      yesterday: "Ontem",
      newMessage: "Nova mensagem",
    },
    en: {
      title: "Messages",
      subtitle: "It's So Clean Website",
      all: "All",
      new: "New",
      replied: "Replied",
      archived: "Archived",
      search: "Search messages...",
      noMessages: "No messages",
      noMessagesDesc: "When someone sends a message through the website, it will appear here.",
      selectMessage: "Select a conversation",
      selectMessageDesc: "Choose a customer from the list to view messages.",
      customer: "Customer",
      address: "Address",
      service: "Service",
      frequency: "Frequency",
      property: "Property",
      bedrooms: "bedrooms",
      bathrooms: "bathrooms",
      carpetedArea: "Carpeted area",
      message: "Message",
      messages: "messages",
      replies: "Replies",
      replyPlaceholder: "Write your reply...",
      send: "Send",
      sending: "Sending...",
      replySent: "Reply sent!",
      markUnread: "Mark as unread",
      archive: "Archive conversation",
      confirmArchive: "Archive all messages?",
      yes: "Yes",
      no: "No",
      today: "Today",
      yesterday: "Yesterday",
      newMessage: "New message",
    },
  }[language] || {
    pt: {},
  };

  // Scroll para o final quando carregar conversa
  useEffect(() => {
    if (selectedConversation && detailBodyRef.current && !loadingDetails) {
      setTimeout(() => {
        if (detailBodyRef.current) {
          detailBodyRef.current.scrollTop = detailBodyRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [selectedConversation, loadingDetails, selectedSubmissionReplies]);

  // Focar no input de resposta
  useEffect(() => {
    if (selectedConversation && replyInputRef.current && !loadingDetails) {
      setTimeout(() => replyInputRef.current?.focus(), 200);
    }
  }, [selectedConversation, loadingDetails]);

  // Handler para enviar resposta (para a submissão mais recente)
  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation) return;
    const latestSubmission = selectedConversation.submissions[selectedConversation.submissions.length - 1];
    const success = await sendReply(latestSubmission.id, replyMessage);
    if (success) {
      setReplyMessage("");
      setReplySuccess(true);
      setTimeout(() => setReplySuccess(false), 3000);
    }
  };

  // Handler para arquivar
  const handleArchive = async (conversation) => {
    await archiveConversation(conversation);
    setShowArchiveConfirm(null);
  };

  // Formatar data relativa
  const formatRelativeDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString(language === "en" ? "en-NZ" : "pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffDays === 1) {
      return t.yesterday;
    } else if (diffDays < 7) {
      return date.toLocaleDateString(language === "en" ? "en-NZ" : "pt-BR", {
        weekday: "short",
      });
    } else {
      return date.toLocaleDateString(language === "en" ? "en-NZ" : "pt-BR", {
        day: "2-digit",
        month: "short",
      });
    }
  };

  // Gerar iniciais do nome
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Gerar cor do avatar baseado no nome (tons de azul harmonizados com navbar)
  const getAvatarColor = (name) => {
    if (!name) return "#6c757d";
    const colors = [
      "#2b8ac4", "#3a9dd4", "#4baee0", "#5bb8e5", "#6bc2ea",
      "#1e7bb5", "#247ba7", "#2d8fc9", "#3699d0", "#40a3d8",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Obter preview da última mensagem
  const getLastMessagePreview = (conversation) => {
    const lastSub = conversation.submissions[conversation.submissions.length - 1];
    return lastSub?.message?.substring(0, 50) + (lastSub?.message?.length > 50 ? "..." : "");
  };

  // Contar mensagens não lidas
  const countUnread = (conversation) => {
    return conversation.submissions.filter(s => s.status === 'new').length;
  };

  // Loading inicial
  if (loading) {
    return (
      <div className="wsp-loading">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="wsp-container">
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible className="wsp-alert">
          {error}
        </Alert>
      )}

      <div className={`wsp-layout${selectedConversation ? ' has-selection' : ''}`}>
        {/* Sidebar - Lista de Conversas */}
        <div className="wsp-sidebar">
          {/* Header da Sidebar */}
          <div className="wsp-sidebar-header">
            <div className="wsp-title-row">
              <h4 className="wsp-title">{t.title}</h4>
              <Button
                variant="link"
                className="wsp-refresh-btn"
                onClick={refresh}
                title="Refresh"
              >
                🔄
              </Button>
            </div>
            <span className="wsp-subtitle">{t.subtitle}</span>

            {/* Stats compactos */}
            <div className="wsp-stats">
              <div className="wsp-stat">
                <span className="wsp-stat-value">{stats.totalUsers}</span>
                <span className="wsp-stat-label">{t.all}</span>
              </div>
              <div className="wsp-stat wsp-stat-new">
                <span className="wsp-stat-value">{stats.newSubmissions}</span>
                <span className="wsp-stat-label">{t.new}</span>
              </div>
              <div className="wsp-stat wsp-stat-replied">
                <span className="wsp-stat-value">{stats.repliedSubmissions}</span>
                <span className="wsp-stat-label">{t.replied}</span>
              </div>
            </div>
          </div>

          {/* Busca */}
          <div className="wsp-search">
            <InputGroup>
              <InputGroup.Text className="wsp-search-icon">🔍</InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t.search}
                value={filters.search}
                onChange={(e) => updateFilters({ search: e.target.value })}
                className="wsp-search-input"
              />
              {filters.search && (
                <Button
                  variant="link"
                  className="wsp-search-clear"
                  onClick={() => updateFilters({ search: "" })}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Filtros como tabs */}
          <div className="wsp-filters">
            <button
              className={`wsp-filter-btn ${!filters.status ? "active" : ""}`}
              onClick={() => updateFilters({ status: "" })}
            >
              {t.all}
            </button>
            <button
              className={`wsp-filter-btn ${filters.status === "new" ? "active" : ""}`}
              onClick={() => updateFilters({ status: "new" })}
            >
              {t.new}
              {stats.newSubmissions > 0 && (
                <span className="wsp-filter-badge">{stats.newSubmissions}</span>
              )}
            </button>
            <button
              className={`wsp-filter-btn ${filters.status === "replied" ? "active" : ""}`}
              onClick={() => updateFilters({ status: "replied" })}
            >
              {t.replied}
            </button>
            <button
              className={`wsp-filter-btn ${filters.status === "archived" ? "active" : ""}`}
              onClick={() => updateFilters({ status: "archived" })}
            >
              {t.archived}
            </button>
          </div>

          {/* Lista de Conversas */}
          <div className="wsp-message-list">
            {loadingSubmissions ? (
              <div className="wsp-loading-list">
                <Spinner animation="border" size="sm" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="wsp-empty">
                <div className="wsp-empty-icon">📭</div>
                <div className="wsp-empty-title">{t.noMessages}</div>
                <div className="wsp-empty-desc">{t.noMessagesDesc}</div>
              </div>
            ) : (
              conversations.map((conversation) => (
                <div
                  key={conversation.clientEmail}
                  className={`wsp-message-item ${
                    selectedConversation?.clientEmail === conversation.clientEmail ? "selected" : ""
                  } ${conversation.hasUnread ? "unread" : ""}`}
                  onClick={() => selectConversation(conversation)}
                >
                  {/* Avatar */}
                  <div
                    className="wsp-avatar"
                    style={{ backgroundColor: getAvatarColor(conversation.clientName) }}
                  >
                    {getInitials(conversation.clientName)}
                  </div>

                  {/* Conteúdo */}
                  <div className="wsp-message-content">
                    <div className="wsp-message-header">
                      <span className="wsp-message-name">
                        {conversation.clientName || "Sem nome"}
                      </span>
                      <span className="wsp-message-date">
                        {formatRelativeDate(conversation.latestDate)}
                      </span>
                    </div>
                    <div className="wsp-message-meta">
                      {conversation.totalMessages > 1 && (
                        <Badge bg="secondary" className="wsp-message-count">
                          {conversation.totalMessages} {t.messages}
                        </Badge>
                      )}
                    </div>
                    <div className="wsp-message-preview">
                      {getLastMessagePreview(conversation)}
                    </div>
                  </div>

                  {/* Indicador de não lidas */}
                  {conversation.hasUnread && (
                    <div className="wsp-unread-badge">
                      {countUnread(conversation)}
                    </div>
                  )}

                  {/* Menu de 3 pontinhos */}
                  <Dropdown
                    className="wsp-message-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown.Toggle as="button" className="wsp-menu-btn">
                      ⋮
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      {!conversation.hasUnread && (
                        <Dropdown.Item onClick={() => markConversationAsUnread(conversation)}>
                          📩 {t.markUnread}
                        </Dropdown.Item>
                      )}
                      <Dropdown.Item
                        className="text-danger"
                        onClick={() => setShowArchiveConfirm(conversation.clientEmail)}
                      >
                        🗑️ {t.archive}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Confirmação de arquivamento */}
                  {showArchiveConfirm === conversation.clientEmail && (
                    <div
                      className="wsp-archive-confirm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>{t.confirmArchive}</span>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleArchive(conversation)}
                      >
                        {t.yes}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setShowArchiveConfirm(null)}
                      >
                        {t.no}
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Painel de Detalhes */}
        <div className="wsp-detail-panel">
          {loadingDetails ? (
            <div className="wsp-loading">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : !selectedConversation ? (
            <div className="wsp-no-selection">
              <div className="wsp-no-selection-icon">💬</div>
              <div className="wsp-no-selection-title">{t.selectMessage}</div>
              <div className="wsp-no-selection-desc">{t.selectMessageDesc}</div>
            </div>
          ) : (
            <>
              {/* Header do Detalhe */}
              <div className="wsp-detail-header">
                <button className="wsp-back-btn" onClick={closeDetails}>
                  ←
                </button>
                <div
                  className="wsp-detail-avatar"
                  style={{ backgroundColor: getAvatarColor(selectedConversation.clientName) }}
                >
                  {getInitials(selectedConversation.clientName)}
                </div>
                <div className="wsp-detail-info">
                  <h5 className="wsp-detail-name">
                    {selectedConversation.clientName || "Sem nome"}
                  </h5>
                  <div className="wsp-detail-contact-card">
                    {selectedConversation.clientPhone && (
                      <a href={`tel:${selectedConversation.clientPhone}`} className="wsp-contact-item wsp-contact-phone">
                        <span className="wsp-contact-icon">📱</span>
                        <span className="wsp-contact-value">{selectedConversation.clientPhone}</span>
                      </a>
                    )}
                    {selectedConversation.clientEmail && (
                      <a href={`mailto:${selectedConversation.clientEmail}`} className="wsp-contact-item wsp-contact-email">
                        <span className="wsp-contact-icon">✉️</span>
                        <span className="wsp-contact-value">{selectedConversation.clientEmail}</span>
                      </a>
                    )}
                    {selectedConversation.clientAddress && (
                      <div className="wsp-contact-item wsp-contact-address">
                        <span className="wsp-contact-icon">📍</span>
                        <span className="wsp-contact-value">{selectedConversation.clientAddress}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="wsp-detail-actions">
                  <Dropdown>
                    <Dropdown.Toggle as="button" className="wsp-action-btn">
                      ⋮
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      {!selectedConversation.hasUnread && (
                        <Dropdown.Item
                          onClick={() => markConversationAsUnread(selectedConversation)}
                        >
                          📩 {t.markUnread}
                        </Dropdown.Item>
                      )}
                      <Dropdown.Item
                        className="text-danger"
                        onClick={() => handleArchive(selectedConversation)}
                      >
                        🗑️ {t.archive}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <button className="wsp-close-btn" onClick={closeDetails}>
                    ✕
                  </button>
                </div>
              </div>

              {/* Corpo do Detalhe - Thread de Mensagens - Estilo Wix */}
              <div className="wsp-detail-body" ref={detailBodyRef}>
                {selectedConversation.submissions.map((submission) => (
                  <div key={submission.id} className="wsp-submission-entry">
                    {/* Timestamp discreto */}
                    <div className="wsp-entry-timestamp">
                      {formatDate(submission.created_at)}
                    </div>

                    {/* Tabela de dados do formulário - estilo Wix */}
                    <table className="wsp-form-table">
                      <tbody>
                        {submission.user_address && (
                          <tr>
                            <td className="wsp-field-label">{t.address}</td>
                            <td className="wsp-field-value">{submission.user_address}</td>
                          </tr>
                        )}
                        <tr>
                          <td className="wsp-field-label">{t.service}</td>
                          <td className="wsp-field-value">
                            {SERVICE_NAMES[submission.service] || submission.service}
                          </td>
                        </tr>
                        {submission.frequency && (
                          <tr>
                            <td className="wsp-field-label">{t.frequency}</td>
                            <td className="wsp-field-value">{submission.frequency}</td>
                          </tr>
                        )}
                        {(submission.bedrooms || submission.bathrooms) && (
                          <tr>
                            <td className="wsp-field-label">{t.property}</td>
                            <td className="wsp-field-value">
                              {submission.bedrooms && <>{submission.bedrooms} {t.bedrooms}</>}
                              {submission.bedrooms && submission.bathrooms && ", "}
                              {submission.bathrooms && <>{submission.bathrooms} {t.bathrooms}</>}
                            </td>
                          </tr>
                        )}
                        {submission.carpeted_area && (
                          <tr>
                            <td className="wsp-field-label">{t.carpetedArea}</td>
                            <td className="wsp-field-value">{submission.carpeted_area}</td>
                          </tr>
                        )}
                        <tr>
                          <td className="wsp-field-label">{t.message}</td>
                          <td className="wsp-field-value wsp-field-message">
                            {submission.message}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Respostas para esta submissão */}
                    {(selectedSubmissionReplies[submission.id] || []).map((reply, rIndex) => (
                      <div key={reply.id || rIndex} className="wsp-admin-reply">
                        <div className="wsp-reply-meta">
                          <span className="wsp-reply-author">
                            {reply.admin_name || "Admin"}
                          </span>
                          <span className="wsp-reply-time">
                            {formatDate(reply.created_at)}
                          </span>
                        </div>
                        <div className="wsp-reply-text">{reply.message}</div>
                      </div>
                    ))}
                  </div>
                ))}

                {/* Sucesso de envio */}
                {replySuccess && (
                  <div className="wsp-reply-success">
                    ✅ {t.replySent}
                  </div>
                )}
              </div>

              {/* Footer - Campo de Resposta */}
              <div className="wsp-detail-footer">
                <Form.Control
                  as="textarea"
                  ref={replyInputRef}
                  rows={3}
                  placeholder={t.replyPlaceholder}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  className="wsp-reply-input"
                  disabled={sendingReply}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.ctrlKey) {
                      handleSendReply();
                    }
                  }}
                />
                <Button
                  className="wsp-send-btn"
                  onClick={handleSendReply}
                  disabled={!replyMessage.trim() || sendingReply}
                >
                  {sendingReply ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {t.sending}
                    </>
                  ) : (
                    <>✉️ {t.send}</>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Container>
  );
}
